'use client';

import { Session } from 'next-auth';
import { useEffect, useMemo, useState } from 'react';

import {
    Alignable,
    Button,
    ButtonList,
    ButtonVariant,
    Column,
    Columns,
    Icon,
    IconName,
    Spacer,
    SpacerSize,
    Title,
    TitleLevels,
    TitleMarkup,
    TypographyAlign,
} from '@bytel/trilogy-react';

import { getServerSession } from '@actions/get-session';

import { ShortListContext } from '@app-types/eligibility';
import { FaiTabsEnum } from '@app-types/home-fai';
import { CategoryCodeInfo } from '@app-types/plan';
import { UserType } from '@app-types/user';

import { BigIcon } from '@components/big';
import { ByouIcon } from '@components/byou';
import { Faq } from '@components/faq';
import { PromoConfiguratorFAI } from '@components/promo-configurator';
import { RocCard } from '@components/roc-card';
import {
    SegmentControl,
    SegmentControlItemType,
    SegmentControlTitle,
    SegmentControlTitleVariant,
} from '@components/segment-control';
import { SSIBlock } from '@components/ssi-block';

import { useMarketings } from '@hooks/use-marketings';

import { emitPriceContextChangeEvent } from '@helpers/marketings';
import { PAGE_SLUG, partials } from '@helpers/partials';

import { PlanTypeId } from '@constants/plan';

import {
    EligibilityOffer,
    EligibilityResults,
    JourneyConfigType,
    getOffersByCategories,
    getShortListOffer,
} from '@eligibility/helpers';

import { ByouFaq } from '../byou-faq';
import { ByouReinsurance } from '../byou-reinsurance';

function hasOffersBigDiscount(offers: EligibilityOffer[]) {
    return offers.some((offer) => offer.hasBigDiscount());
}

interface EligibilityResultTabsProps {
    results: EligibilityResults;
    journeyConfig: JourneyConfigType;
    shortList?: ShortListContext;
    categoryCodeInfo: CategoryCodeInfo[];
    onAddToCart: (gencode: string) => void;
    onTabChange: () => void;
}

export function EligibilityResultSegmentControl({
    results,
    journeyConfig,
    shortList,
    categoryCodeInfo,
    onAddToCart,
    onTabChange,
}: Readonly<EligibilityResultTabsProps>) {
    const [currentTab, setCurrentTab] = useState<FaiTabsEnum | undefined>(FaiTabsEnum.BIG);
    const [marketingsState, _setMarketingsState] = useMarketings();
    const [session, setSession] = useState<Session | null>(null);
    const [shouldShowAllOffers, setShouldShowAllOffers] = useState<boolean>(false);

    const user = session?.user as UserType | undefined;

    const isPackAvailable = useMemo(
        () => journeyConfig.showPackTabs && !!results.offers?.FTTH,
        [journeyConfig, results],
    );

    const allOffers = Object.values(results.offers).flat();
    const shortListOffer = getShortListOffer(allOffers, shortList);
    const offersByCategories = getOffersByCategories(allOffers, categoryCodeInfo, shortListOffer);

    const setTab = (tab: FaiTabsEnum) => {
        setCurrentTab(tab);
        window.location.hash = tab;
        onTabChange();
    };

    const segmentControlItems: SegmentControlItemType[] = useMemo(() => {
        const isBigDisabled =
            (offersByCategories[PlanTypeId.FAI_BOX_INTERNET]?.offers.length ?? 0) === 0 &&
            (offersByCategories[PlanTypeId.FAI_5GBOX]?.offers.length ?? 0) === 0 &&
            !isPackAvailable;
        const isByouDisabled = (offersByCategories[PlanTypeId.FAI_PUREBYOU]?.offers.length ?? 0) === 0;

        const items: SegmentControlItemType[] = [
            {
                key: FaiTabsEnum.BIG,
                title: (
                    <SegmentControlTitle
                        icon={BigIcon}
                        active={currentTab === FaiTabsEnum.BIG}
                        variant={SegmentControlTitleVariant.BIG}
                    />
                ),
                subtitle: 'Avec avantages',
                onClick: () => setTab(FaiTabsEnum.BIG),
                className: 'big',
                active: currentTab === FaiTabsEnum.BIG && !isBigDisabled,
                disabled: isBigDisabled,
            },
            {
                key: FaiTabsEnum.PURE_BYOU,
                title: (
                    <SegmentControlTitle
                        icon={ByouIcon}
                        active={currentTab === FaiTabsEnum.PURE_BYOU}
                        variant={SegmentControlTitleVariant.BYOU}
                    />
                ),
                subtitle: 'Sans engagement',
                onClick: () => setTab(FaiTabsEnum.PURE_BYOU),
                className: 'byou',
                active: (currentTab === FaiTabsEnum.PURE_BYOU || isBigDisabled) && !isByouDisabled,
                disabled: isByouDisabled,
            },
        ];

        return items;
    }, [currentTab, setCurrentTab, offersByCategories, isPackAvailable]);

    useEffect(() => {
        getServerSession()
            .then((_session) => {
                setSession(_session);
            })
            .catch(() => setSession(null));

        if (window.location.hash) {
            const hash = window.location.hash.replace('#', '').toLowerCase();
            if (hash === FaiTabsEnum.BIG || hash === FaiTabsEnum.PURE_BYOU) {
                setCurrentTab(hash as FaiTabsEnum);
            }
        }
    }, []);

    if (Object.keys(results.offers).length === 0) {
        return null;
    }

    return (
        <>
            {shortListOffer && (
                <>
                    {!user && hasOffersBigDiscount([shortListOffer]) && (
                        <PromoConfiguratorFAI
                            isFullwidth
                            marketingsState={marketingsState}
                            setMarketingsState={(marketings) => {
                                const newMarketingsState = _setMarketingsState(marketings);
                                emitPriceContextChangeEvent(newMarketingsState);
                                return newMarketingsState;
                            }}
                        />
                    )}
                    <Spacer size={SpacerSize.FOUR} />
                    <Columns align={Alignable.ALIGNED_CENTER} gap={2} multiline>
                        <Column
                            mobileSize={12}
                            tabletSize={6}
                            desktopSize={4}
                            data-cy={`${shortListOffer.rangeNg}_${shortListOffer.commercialSeries}`}
                        >
                            <RocCard
                                plan={shortListOffer.mapToFaiType()}
                                cta={{
                                    onClick: () => onAddToCart(shortListOffer.sku),
                                    text: 'Ajouter au panier',
                                    color: ButtonVariant.CONVERSION,
                                }}
                                hasSiblingHat={shortListOffer.hat !== undefined}
                                user={user}
                            />
                        </Column>
                    </Columns>
                    <ButtonList align={Alignable.ALIGNED_CENTER}>
                        <Button variant={ButtonVariant.GHOST} onClick={() => setShouldShowAllOffers((prev) => !prev)}>
                            {shouldShowAllOffers ? (
                                <Icon name={IconName.ARROW_UP} />
                            ) : (
                                <Icon name={IconName.ARROW_DOWN} />
                            )}{' '}
                            Afficher les autres offres
                        </Button>
                    </ButtonList>
                </>
            )}
            {(!shortList || shouldShowAllOffers) && (
                <>
                    <SegmentControl items={segmentControlItems} />
                    {currentTab === FaiTabsEnum.BIG && (
                        <>
                            {offersByCategories[PlanTypeId.FAI_BOX_INTERNET] &&
                            offersByCategories[PlanTypeId.FAI_BOX_INTERNET].offers.length > 0 ? (
                                <>
                                    {!user &&
                                        hasOffersBigDiscount(offersByCategories[PlanTypeId.FAI_BOX_INTERNET].offers) &&
                                        (!shortListOffer || !hasOffersBigDiscount([shortListOffer])) && (
                                            <PromoConfiguratorFAI
                                                isFullwidth
                                                marketingsState={marketingsState}
                                                setMarketingsState={(marketings) => {
                                                    const newMarketingsState = _setMarketingsState(marketings);
                                                    emitPriceContextChangeEvent(newMarketingsState);
                                                    return newMarketingsState;
                                                }}
                                            />
                                        )}
                                    <Spacer size={SpacerSize.FOUR} />
                                    <Columns align={Alignable.ALIGNED_CENTER} gap={2} multiline>
                                        {offersByCategories[PlanTypeId.FAI_BOX_INTERNET].offers?.map((offer) => (
                                            <Column
                                                key={offer.sku}
                                                mobileSize={12}
                                                tabletSize={6}
                                                desktopSize={4}
                                                data-cy={`${offer.rangeNg}_${offer.commercialSeries}`}
                                            >
                                                <RocCard
                                                    plan={offer.mapToFaiType()}
                                                    cta={{
                                                        onClick: () => onAddToCart(offer.sku),
                                                        text: 'Ajouter au panier',
                                                        color: ButtonVariant.CONVERSION,
                                                    }}
                                                    hasSiblingHat={
                                                        offersByCategories[PlanTypeId.FAI_BOX_INTERNET]?.hasHat ?? false
                                                    }
                                                    user={user}
                                                />
                                            </Column>
                                        ))}
                                    </Columns>
                                </>
                            ) : (
                                <Spacer size={SpacerSize.FOUR} />
                            )}
                            {isPackAvailable && <SSIBlock tagId='offres-combo' url={partials.MARKETING.FAI_PACK} />}
                            {offersByCategories[PlanTypeId.FAI_5GBOX] &&
                                offersByCategories[PlanTypeId.FAI_5GBOX].offers.length > 0 && (
                                    <>
                                        <Title level={TitleLevels.THREE} typo={TypographyAlign.TEXT_CENTERED}>
                                            Nos offres 5G box
                                        </Title>
                                        <Columns align={Alignable.ALIGNED_CENTER} gap={2} multiline>
                                            {offersByCategories[PlanTypeId.FAI_5GBOX].offers.map((offer) => (
                                                <Column
                                                    key={offer.sku}
                                                    mobileSize={12}
                                                    tabletSize={6}
                                                    desktopSize={4}
                                                    data-cy={`${offer.rangeNg}_${offer.commercialSeries}`}
                                                >
                                                    <RocCard
                                                        plan={offer.mapToFaiType()}
                                                        cta={{
                                                            onClick: () => onAddToCart(offer.sku),
                                                            text: 'Ajouter au panier',
                                                            color: ButtonVariant.CONVERSION,
                                                        }}
                                                        hasSiblingHat={
                                                            offersByCategories[PlanTypeId.FAI_5GBOX]?.hasHat ?? false
                                                        }
                                                        user={user}
                                                    />
                                                </Column>
                                            ))}
                                        </Columns>
                                    </>
                                )}

                            <Faq
                                id='faq-offres-internet'
                                titleMarkup={TitleMarkup.H3}
                                title='Vos questions fréquentes sur l’éligibilité à la fibre'
                                url={PAGE_SLUG.ELIGIBILITY}
                            />
                        </>
                    )}
                    {currentTab === FaiTabsEnum.PURE_BYOU && (
                        <>
                            <Spacer size={SpacerSize.FOUR} />
                            <Columns align={Alignable.ALIGNED_CENTER} gap={2} multiline>
                                {offersByCategories[PlanTypeId.FAI_PUREBYOU]?.offers.map((offer) => (
                                    <Column
                                        key={offer.sku}
                                        mobileSize={12}
                                        tabletSize={6}
                                        desktopSize={4}
                                        data-cy={`${offer.rangeNg}_${offer.commercialSeries}`}
                                    >
                                        <RocCard
                                            plan={offer.mapToFaiType()}
                                            cta={{
                                                onClick: () => onAddToCart(offer.sku),
                                                text: 'Ajouter au panier',
                                                color: ButtonVariant.CONVERSION,
                                            }}
                                            hasSiblingHat={offersByCategories[PlanTypeId.FAI_PUREBYOU]?.hasHat ?? false}
                                            user={user}
                                        />
                                    </Column>
                                ))}
                            </Columns>
                            <Columns>
                                <Column className='byou'>
                                    <Spacer size={SpacerSize.FIVE} />
                                    <ByouReinsurance />
                                </Column>
                            </Columns>
                            <Columns>
                                <Column className='byou'>
                                    <Spacer size={SpacerSize.FIVE} />
                                    <ByouFaq />
                                </Column>
                            </Columns>
                        </>
                    )}
                </>
            )}
        </>
    );
}
